/**
 * CommercialProductsPanel
 *
 * Middle column — shows commercial products.
 *
 * Two modes:
 *  - No package selected: flat list of ALL commercial products. Selecting one
 *    loads the right column. No link/unlink controls.
 *  - Package selected: shows LINKED group (with unlink) and AVAILABLE group
 *    (with link button). Same selection behaviour for right column.
 */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import './CommercialProductsPanel.css';

const CommercialProductsPanel = ({
  selectedPackage,
  allProducts,
  linked,
  available,
  selectedId,
  onSelect,
  onLink,
  onUnlink,
  onCreateNew,
  loading,
  isAdmin,
}) => {
  const [confirmingUnlinkId, setConfirmingUnlinkId] = useState(null);

  const subtitle = selectedPackage
    ? `linked to: ${selectedPackage.product_name}`
    : 'Select a product to manage links';

  const renderItem = (prod, showUnlink = false) => {
    if (showUnlink && confirmingUnlinkId === prod.id) {
      return (
        <li key={prod.id} className="ph-panel__item-row">
          <div className="ph-panel__unlink-confirm">
            <span className="ph-panel__unlink-confirm-text">
              Unlink <strong>{prod.product_name}</strong>?
            </span>
            <button
              className="ph-panel__unlink-confirm-yes"
              onClick={() => { setConfirmingUnlinkId(null); onUnlink(prod.id); }}
              type="button"
            >
              Confirm
            </button>
            <button
              className="ph-panel__unlink-confirm-no"
              onClick={() => setConfirmingUnlinkId(null)}
              type="button"
            >
              Cancel
            </button>
          </div>
        </li>
      );
    }

    return (
      <li key={prod.id} className="ph-panel__item-row">
        <button
          className={`ph-panel__item ph-panel__item--linked ${selectedId === prod.id ? 'ph-panel__item--active' : ''}`}
          onClick={() => onSelect(prod)}
          type="button"
        >
          <span className="ph-panel__item-name">{prod.product_name}</span>
          {prod.technicalLinkedCount > 0 && (
            <span className="ph-panel__item-badge">{prod.technicalLinkedCount}</span>
          )}
          <svg className="ph-panel__item-chevron" width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
          </svg>
        </button>
        {showUnlink && isAdmin && (
          <button
            className="ph-panel__unlink-btn"
            onClick={() => setConfirmingUnlinkId(prod.id)}
            title="Unlink from package"
            type="button"
          >
            ×
          </button>
        )}
      </li>
    );
  };

  return (
    <div className="ph-panel">
      <div className="ph-panel__header">
        <div>
          <h3 className="ph-panel__title">Commercial Products</h3>
          <p className="ph-panel__subtitle">{subtitle}</p>
        </div>
        <span className="ph-panel__count">
          {selectedPackage ? linked.length : allProducts.length}
        </span>
      </div>

      <div className="ph-panel__body">
        {loading ? (
          <div className="ph-panel__loading">
            <div className="ph-panel__spinner" />
          </div>
        ) : selectedPackage ? (
          /* ── Package selected: linked / available split ── */
          <>
            {linked.length === 0 && available.length === 0 ? (
              <div className="ph-panel__empty">No commercial products exist. Create one to get started.</div>
            ) : (
              <>
                {linked.length > 0 && (
                  <>
                    <div className="ph-panel__section-label">Linked</div>
                    <ul className="ph-panel__list">
                      {linked.map((prod) => renderItem(prod, true))}
                    </ul>
                  </>
                )}

                {available.length > 0 && (
                  <div className="ph-panel__available">
                    <div className="ph-panel__available-header">Available to Link</div>
                    <ul className="ph-panel__list">
                      {available.map((prod) => (
                        <li key={prod.id}>
                          <button
                            className={`ph-panel__item ph-panel__item--available ${selectedId === prod.id ? 'ph-panel__item--active' : ''}`}
                            onClick={() => onSelect(prod)}
                            type="button"
                          >
                            <span className="ph-panel__item-name">{prod.product_name}</span>
                            {isAdmin && (
                              <span
                                className="ph-panel__item-link-icon"
                                role="button"
                                tabIndex={0}
                                title="Link to package"
                                onClick={(e) => { e.stopPropagation(); onLink(prod.id); }}
                                onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); onLink(prod.id); } }}
                              >
                                +
                              </span>
                            )}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            )}

            {isAdmin && (
              <div className="ph-panel__actions">
                <button className="ph-panel__create-btn" onClick={onCreateNew} type="button">
                  + New Commercial Product
                </button>
              </div>
            )}
          </>
        ) : (
          /* ── No package selected: flat list of all commercial products ── */
          <>
            {allProducts.length === 0 ? (
              <div className="ph-panel__empty">
                <p>No commercial products found.</p>
                {isAdmin && (
                  <button className="ph-panel__empty-create-btn" onClick={onCreateNew} type="button">
                    + New Commercial Product
                  </button>
                )}
              </div>
            ) : (
              <>
                <ul className="ph-panel__list">
                  {allProducts.map((prod) => renderItem(prod, false))}
                </ul>
                {isAdmin && (
                  <div className="ph-panel__actions">
                    <button className="ph-panel__create-btn" onClick={onCreateNew} type="button">
                      + New Commercial Product
                    </button>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

CommercialProductsPanel.propTypes = {
  selectedPackage: PropTypes.object,
  allProducts: PropTypes.array.isRequired,
  linked: PropTypes.array.isRequired,
  available: PropTypes.array.isRequired,
  selectedId: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  onLink: PropTypes.func.isRequired,
  onUnlink: PropTypes.func.isRequired,
  onCreateNew: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  isAdmin: PropTypes.bool,
};

export default CommercialProductsPanel;
